import { createContext, useContext, useEffect, useState } from "react";
import { API } from "../lib/api";
import { getCachedUserSettings } from "../lib/userSettingsCache";
import { useAuth } from "./AuthContext";
import "../theme/dark-theme.css";
import "../theme/glass-theme.css";

const STORAGE_KEY = "hive-theme";
const DEFAULT_THEME = "default";
const THEMES = {
  DEFAULT: "default",
  DARK: "dark",
  GLASS: "glass",
};
const ACTIVE_THEMES = new Set(Object.values(THEMES));

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  isDarkMode: false,
  setTheme: () => {},
});

const applyDocumentTheme = (theme) => {
  if (typeof document === "undefined") {
    return;
  }

  const html = document.documentElement;
  const metaTheme = document.querySelector('meta[name="theme-color"]');
  const isDarkMode = theme === THEMES.DARK || theme === THEMES.GLASS;
  const isGlassMode = theme === THEMES.GLASS;

  if (isDarkMode) {
    html.setAttribute("data-hive-theme", "dark");
    html.setAttribute("data-bs-theme", "dark");
    html.setAttribute("data-pc-theme", "dark");
  } else {
    html.removeAttribute("data-hive-theme");
    html.removeAttribute("data-bs-theme");
    html.removeAttribute("data-pc-theme");
  }

  if (isGlassMode) {
    html.setAttribute("data-hive-variant", THEMES.GLASS);
  } else {
    html.removeAttribute("data-hive-variant");
  }

  if (metaTheme) {
    metaTheme.setAttribute(
      "content",
      isGlassMode ? "#d8ebff" : isDarkMode ? "#111827" : "#0286b1"
    );
  }
};


const resolveTheme = (theme) => (ACTIVE_THEMES.has(theme) ? theme : DEFAULT_THEME);

export const ThemeProvider = ({ children }) => {
  const { user, loading } = useAuth();
  const [theme, setThemeState] = useState(() => resolveTheme(localStorage.getItem(STORAGE_KEY)));
  const resolvedUserId = user?.userId || user?.id || localStorage.getItem("id");


  useEffect(() => {
    applyDocumentTheme(theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!resolvedUserId) {
      setThemeState(DEFAULT_THEME);
      return;
    }

    let ignore = false;

    async function loadThemePreference() {
      const settings = await getCachedUserSettings(
        resolvedUserId,
        () => API.Settings.FetchByUser(resolvedUserId)
      );
      const nextTheme = resolveTheme(settings?.theme);

      if (!ignore) {
        setThemeState(nextTheme);
      }
    }

    loadThemePreference();

    return () => {
      ignore = true;
    };
  }, [loading, resolvedUserId]);

  const setTheme = (nextTheme) => {
    setThemeState(resolveTheme(nextTheme));
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        isDarkMode: theme === THEMES.DARK || theme === THEMES.GLASS,
        setTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemePreference = () => useContext(ThemeContext);
